import {useEffect} from 'react';

import type {ShellViewer} from '../account/accountModel';
import {
  isInternalUserEmail,
  setViewerUsingAdminPrivileges,
  viewerUsingAdminPrivileges,
  viewingAs,
} from './adminState';
import {teleportAdminCheckEnabled} from '../shellConfig';
import {useTeleportAccessRequests} from './useTeleportAccessRequests';

export type AdminTools = {
  isAdmin: boolean;
  isWbAdmin: boolean;
  isInternalUser: boolean;
  isImpersonating: boolean;
  teleportAdminCheckIsEnabled: boolean;
  shouldShowAdminTools: boolean;
  isAdminModeActive: boolean;
  approvedTeleportAccessRequests: Record<string, string>;
  isFetchingTeleportAccessRequests: boolean;
};

export function useAdminTools(viewer: ShellViewer | null): AdminTools {
  const isAdmin = viewer?.admin === true;
  const isInternalUser = isInternalUserEmail(viewer?.email);
  const isWbAdmin = isAdmin && isInternalUser;
  const isImpersonating = viewingAs() !== '';
  const teleportAdminCheckIsEnabled = teleportAdminCheckEnabled();
  const {
    approvedTeleportAccessRequests,
    isFetching: isFetchingTeleportAccessRequests,
  } = useTeleportAccessRequests(isWbAdmin);
  const hasApprovedRequest =
    Object.keys(approvedTeleportAccessRequests).length > 0;
  const shouldShowAdminTools =
    !teleportAdminCheckIsEnabled || hasApprovedRequest;
  const isAdminMode = isAdmin && viewerUsingAdminPrivileges();

  // Wait for the access request fetch before unsetting admin privileges.
  // Otherwise the cookie could be cleared before the request returns.
  const shouldUnsetAdminPrivileges =
    isWbAdmin &&
    !isFetchingTeleportAccessRequests &&
    teleportAdminCheckIsEnabled &&
    isAdminMode &&
    !hasApprovedRequest &&
    !isImpersonating;

  // During impersonation, the viewer can be null initially. Without isWbAdmin,
  // that could clear the cookie before the viewer loads. Do not clear it while
  // impersonating because the viewer may be another W&B admin.
  useEffect(() => {
    if (shouldUnsetAdminPrivileges) {
      setViewerUsingAdminPrivileges(false);
    }
  }, [shouldUnsetAdminPrivileges]);

  return {
    isAdmin,
    isWbAdmin,
    isInternalUser,
    isImpersonating,
    teleportAdminCheckIsEnabled,
    shouldShowAdminTools,
    isAdminModeActive: isAdminMode && !shouldUnsetAdminPrivileges,
    approvedTeleportAccessRequests,
    isFetchingTeleportAccessRequests,
  };
}
